import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_BASE = '/machinesim/api';

function siteLabel() {
  return import.meta.env.VITE_SITE_NAME || 'Chentronix';
}

export default function StreamMachineList() {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/machines`);
        if (!cancelled) {
          setMachines(res.data.machines || []);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load machines');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-foreground">
        <p className="text-lg">Loading machines…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16 text-destructive px-4">
        <p>{error}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-secondary rounded hover:bg-accent text-foreground"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="text-foreground p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        <header className="mb-8">
          <h2 className="text-2xl font-bold mb-2">Machines</h2>
          <p className="text-muted-foreground text-sm max-w-2xl">
            Machines listed here match the simulator API and the UNS topics published by the server (
            <span className="font-mono text-primary">
              {siteLabel()}
              /&lt;machine&gt;/state/…
            </span>
            ). No authentication; local API only.
          </p>
        </header>

        <ul className="grid gap-3 sm:grid-cols-2">
          {machines.map((m) => (
            <li key={m.name}>
              <Link
                to={`machines/${encodeURIComponent(m.name)}`}
                className="block rounded-lg border border-border bg-card p-4 hover:border-primary hover:bg-accent transition-colors"
              >
                <div className="font-mono font-semibold text-lg text-foreground">{m.name}</div>
                <div className="text-sm text-muted-foreground mt-1">
                  execution:{' '}
                  <span
                    className={
                      m.state?.execution === 'ACTIVE' ? 'text-cn-success' : 'text-cn-warning'
                    }
                  >
                    {m.state?.execution ?? '—'}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>

        {machines.length === 0 && (
          <p className="text-muted-foreground text-center py-12">No machines returned from the API.</p>
        )}
      </div>
    </div>
  );
}
